import { useState, useEffect } from "react";
import { Form, Input, Button, Typography, Divider, message, Checkbox, Space, Modal } from "antd";
import { LockOutlined, ApiOutlined, EyeInvisibleOutlined, EyeOutlined, MailOutlined, SafetyOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { API_BASE } from "./config";
import { useNavigate } from "react-router-dom";
import { useThemeContext } from "./context/ThemeContext";
import coreItLogo from "./assets/CoreIT.png";

const { Title, Text, Paragraph } = Typography;

const Login = () => {
  const isDark = true;
  const [loading, setLoading] = useState(false);
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      form.setFieldsValue({ email: savedEmail, remember: true });
    } else {
      form.setFieldsValue({ remember: false });
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [form]);

  const handleLogin = async (values) => {
    setLoading(true);
      try {
          const  loginEndpoint  = `${ API_BASE }/Auth/login`;
          const res = await fetch( loginEndpoint , {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      setLoading(false);

      if (res.ok && data.token && data.dbName) {
        localStorage.setItem("user", JSON.stringify(data));
        localStorage.setItem("token", data.token);
        localStorage.setItem("dbName", data.dbName);

        if (values.remember) {
          localStorage.setItem("rememberedEmail", values.email);
        } else {
          localStorage.removeItem("rememberedEmail");
        }

        // Kampanya durumunu kontrol et
        try {
          const campaignRes = await fetch(`${API_BASE}/Campaign/check-status`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              DbName: data.dbName,
            },
          });
          if (campaignRes.ok) {
            const campaignData = await campaignRes.json();
            if (campaignData.changes && campaignData.changes.length > 0) {
              campaignData.changes.forEach(change => {
                const changeType = change.changeType === 'Started' ? 'başladı' : 'sona erdi';
                message.info(`"${change.campaignName}" kampanyası ${changeType}`);
              });
            }
          }
        } catch (error) {
          console.error('Campaign status check failed:', error);
        }

        // Uygulamanın geri kalanına "giriş yapıldı" haberini yayınla.
        window.dispatchEvent(new CustomEvent('loginSuccess'));

        navigate("/");
      } else {
        message.error("Giriş başarısız veya tenant (dbName) bilgisi gelmedi!");
      }
    } catch {
      setLoading(false);
      message.error("Sunucuya bağlanılamadı!");
    }
  };

  const handleForgotPassword = () => {
    const email = form.getFieldValue("email");
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      message.warning("Lütfen önce geçerli bir e-posta adresi girin");
      return;
    }
    setForgotPasswordModalOpen(true);
  };

  const handleSendResetEmail = async () => {
    const email = form.getFieldValue("email");
    setSendingResetEmail(true);

    try {
      // Backend hazır olunca bu endpoint kullanılacak
      // const forgotPasswordEndpoint = `${API_BASE}/Auth/forgot-password`;
      // const res = await fetch(forgotPasswordEndpoint, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ email }),
      // });
      // const data = await res.json();
      // if (res.ok) {
      //   message.success("Şifre sıfırlama bağlantısı e-posta adresinize gönderildi!");
      //   setForgotPasswordModalOpen(false);
      // } else {
      //   message.error(data.message || "Bir hata oluştu. Lütfen tekrar deneyin.");
      // }

      // Demo için 2 saniye bekle
      await new Promise(resolve => setTimeout(resolve, 2000));
      message.success(`${email} adresine şifre sıfırlama bağlantısı gönderildi!`);
      setForgotPasswordModalOpen(false);
    } catch (error) {
      message.error("Sunucuya bağlanılamadı! Lütfen tekrar deneyin.");
    } finally {
      setSendingResetEmail(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 50%, #16213e 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        transition: "background 0.5s ease",
      }}
    >
      {/* Animated background elements */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          opacity: 0.1,
          background: "radial-gradient(circle at 20% 50%, #667eea 0%, transparent 50%), radial-gradient(circle at 80% 50%, #f093fb 0%, transparent 50%)",
        }}
      />

      <div
        style={{
          maxWidth: "1100px",
          width: isMobile ? "95%" : "90%",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.6)",
          borderRadius: isMobile ? "16px" : "24px",
          overflow: "hidden",
          backgroundColor: "#1e1e2f",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left side - Brand/Visual Panel */}
        <div
          style={{
            flex: isMobile ? "none" : "0 0 450px",
            width: isMobile ? "100%" : "auto",
            background: "linear-gradient(135deg, #2d3561 0%, #1f2544 100%)",
            padding: isMobile ? "40px 20px" : "60px 40px",
            display: isMobile ? "none" : "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            color: "#fff",
            position: "relative",
            minHeight: isMobile ? "auto" : "600px",
          }}
        >
          {/* Decorative circles */}
          <div
            style={{
              position: "absolute",
              width: "400px",
              height: "400px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.05)",
              top: "-150px",
              left: "-150px",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "300px",
              height: "300px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.05)",
              bottom: "-100px",
              right: "-100px",
            }}
          />

          <div style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
            {/* Logo */}
            <div
              style={{
                marginBottom: "40px",
                animation: "fadeInDown 1s ease-out",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={coreItLogo}
                alt="Core IT Logo"
                style={{
                  width: "240px",
                  height: "auto",
                  display: "block",
                  objectFit: "contain",
                  filter: isDark ? "brightness(1.2)" : "brightness(1.1) drop-shadow(0 4px 12px rgba(0,0,0,0.15))",
                }}
              />
            </div>

            <Title
              level={1}
              style={{
                color: "#fff",
                fontWeight: 800,
                fontSize: "42px",
                marginBottom: "16px",
                letterSpacing: "-1px",
                textShadow: "0 2px 10px rgba(0,0,0,0.2)",
              }}
            >
              Core Optica
            </Title>

            <Paragraph
              style={{
                color: "rgba(255,255,255,0.9)",
                fontSize: "18px",
                marginBottom: "40px",
                lineHeight: "1.6",
                maxWidth: "400px",
              }}
            >
              Optik perakende yönetiminde yeni nesil çözümler. İşinizi Optica ile geleceğe taşıyın.
            </Paragraph>

            {/* Feature badges */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
              <Space style={{ color: "rgba(255,255,255,0.85)", fontSize: "15px" }}>
                <SafetyOutlined style={{ fontSize: "18px" }} />
                <span>Güvenli ve Hızlı</span>
              </Space>
              <Space style={{ color: "rgba(255,255,255,0.85)", fontSize: "15px" }}>
                <ApiOutlined style={{ fontSize: "18px" }} />
                <span>Bulut Tabanlı Sistem</span>
              </Space>
            </div>

            <Divider style={{ borderColor: "rgba(255,255,255,0.2)", margin: "40px 0 20px" }} />

            <Text
              style={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "13px",
                display: "block",
              }}
            >
              © 2025 Core IT Bilişim A.Ş.
              <br />
              Tüm hakları saklıdır.
            </Text>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div
          style={{
            flex: "1",
            padding: isMobile ? "30px 20px" : "60px 50px",
            backgroundColor: isDark ? "#1e1e2f" : "#ffffff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: isMobile ? "100vh" : "600px",
          }}
        >
          <div style={{ maxWidth: "400px", margin: "0 auto", width: "100%" }}>
            {/* Welcome text */}
            <div style={{ marginBottom: isMobile ? "30px" : "40px" }}>
              <Title
                level={2}
                style={{
                  color: isDark ? "#fff" : "#1a1a2e",
                  fontWeight: 700,
                  fontSize: isMobile ? "24px" : "32px",
                  marginBottom: "8px",
                }}
              >
                Hoş Geldiniz
              </Title>
              <Text
                style={{
                  color: isDark ? "rgba(255,255,255,0.6)" : "#666",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                Devam etmek için giriş yapın
              </Text>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleLogin}
              autoComplete="off"
            >
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: "E-posta giriniz" },
                  { type: "email", message: "Geçerli bir e-posta giriniz" },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "16px" }} />}
                  size="large"
                  placeholder="E-posta adresiniz"
                  autoFocus
                  style={{
                    borderRadius: "12px",
                    padding: "12px 16px",
                    fontSize: "15px",
                    backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                    border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                    color: isDark ? "#fff" : "#333",
                    transition: "all 0.3s ease",
                  }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: "Şifre giriniz" }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "16px" }} />}
                  iconRender={(visible) =>
                    visible ? (
                      <EyeOutlined style={{ color: isDark ? "#667eea" : "#764ba2" }} />
                    ) : (
                      <EyeInvisibleOutlined style={{ color: isDark ? "#667eea" : "#764ba2" }} />
                    )
                  }
                  size="large"
                  placeholder="Şifreniz"
                  style={{
                    borderRadius: "12px",
                    padding: "12px 16px",
                    fontSize: "15px",
                    backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                    border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                    color: isDark ? "#fff" : "#333",
                    transition: "all 0.3s ease",
                  }}
                />
              </Form.Item>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <Form.Item name="remember" valuePropName="checked" style={{ margin: 0 }}>
                  <Checkbox
                    style={{
                      color: isDark ? "rgba(255,255,255,0.8)" : "#666",
                      fontSize: "14px",
                    }}
                  >
                    Beni Hatırla
                  </Checkbox>
                </Form.Item>

                <Button
                  type="link"
                  style={{
                    color: isDark ? "#667eea" : "#764ba2",
                    fontSize: "14px",
                    padding: 0,
                    fontWeight: 500,
                  }}
                  onClick={handleForgotPassword}
                >
                  Şifremi Unuttum?
                </Button>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                style={{
                  background: isDark
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  borderRadius: "12px",
                  height: "50px",
                  fontSize: "16px",
                  fontWeight: 600,
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                  transition: "all 0.3s ease",
                  marginBottom: "16px",
                }}
              >
                Giriş Yap
              </Button>

              <div style={{ textAlign: "center" }}>
                <Text style={{ color: isDark ? "rgba(255,255,255,0.6)" : "#999", fontSize: "14px" }}>
                  Hesabınız yok mu?{" "}
                </Text>
                <Button
                  type="link"
                  style={{
                    color: isDark ? "#667eea" : "#764ba2",
                    fontSize: "14px",
                    padding: "0 4px",
                    fontWeight: 600,
                  }}
                  onClick={() => navigate("/register")}
                >
                  Kayıt Ol
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>

      {/* Add keyframe animations */}
      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Remove autocomplete white background */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${isDark ? '#2a2a3e' : '#f8f9fa'} inset !important;
          -webkit-text-fill-color: ${isDark ? '#fff' : '#333'} !important;
          transition: background-color 5000s ease-in-out 0s;
        }

        input[type="password"]:-webkit-autofill,
        input[type="password"]:-webkit-autofill:hover,
        input[type="password"]:-webkit-autofill:focus,
        input[type="password"]:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${isDark ? '#2a2a3e' : '#f8f9fa'} inset !important;
          -webkit-text-fill-color: ${isDark ? '#fff' : '#333'} !important;
          transition: background-color 5000s ease-in-out 0s;
        }

        /* Placeholder colors - matched with Register page */
        input::placeholder,
        .ant-input::placeholder,
        .ant-input-password input::placeholder {
          color: ${isDark ? 'rgba(255, 255, 255, 0.48)' : 'rgba(0, 0, 0, 0.48)'} !important;
          opacity: 1 !important;
        }

        .ant-form-item input::placeholder {
          color: ${isDark ? 'rgba(255, 255, 255, 0.48)' : 'rgba(0, 0, 0, 0.48)'} !important;
          opacity: 1 !important;
        }
      `}</style>

      {/* Forgot Password Modal */}
      <Modal
        open={forgotPasswordModalOpen}
        onCancel={() => setForgotPasswordModalOpen(false)}
        footer={null}
        centered
        width={480}
        styles={{
          content: {
            backgroundColor: isDark ? "#1e1e2f" : "#ffffff",
            borderRadius: "16px",
            padding: 0,
          },
          body: {
            padding: "32px",
          },
          mask: {
            backdropFilter: "blur(8px)",
            backgroundColor: isDark ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.45)",
          },
        }}
      >
        <div style={{ textAlign: "center" }}>
          {/* Icon */}
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: isDark
                ? "linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)"
                : "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <QuestionCircleOutlined
              style={{
                fontSize: "32px",
                color: isDark ? "#667eea" : "#764ba2",
              }}
            />
          </div>

          {/* Title */}
          <Title
            level={3}
            style={{
              color: isDark ? "#fff" : "#1a1a2e",
              fontWeight: 600,
              marginBottom: "12px",
              fontSize: "22px",
            }}
          >
            Şifre Sıfırlama
          </Title>

          {/* Description */}
          <Paragraph
            style={{
              color: isDark ? "rgba(255,255,255,0.7)" : "#666",
              fontSize: "15px",
              marginBottom: "24px",
              lineHeight: "1.6",
            }}
          >
            <strong style={{ color: isDark ? "#667eea" : "#764ba2" }}>
              {form.getFieldValue("email")}
            </strong>{" "}
            adresine şifre sıfırlama bağlantısı gönderelim mi?
          </Paragraph>

          {/* Info Box */}
          <div
            style={{
              backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
              border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "24px",
              textAlign: "left",
            }}
          >
            <Text
              style={{
                color: isDark ? "rgba(255,255,255,0.6)" : "#666",
                fontSize: "13px",
                lineHeight: "1.6",
              }}
            >
              <SafetyOutlined style={{ marginRight: "8px", color: isDark ? "#667eea" : "#764ba2" }} />
              E-postanıza gönderilecek bağlantı 24 saat geçerli olacaktır. Spam klasörünüzü kontrol etmeyi unutmayın.
            </Text>
          </div>

          {/* Buttons */}
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <Button
              type="primary"
              size="large"
              block
              loading={sendingResetEmail}
              onClick={handleSendResetEmail}
              style={{
                background: isDark
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                borderRadius: "12px",
                height: "48px",
                fontSize: "15px",
                fontWeight: 600,
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              }}
            >
              Evet, Gönder
            </Button>
            <Button
              size="large"
              block
              onClick={() => setForgotPasswordModalOpen(false)}
              style={{
                borderRadius: "12px",
                height: "48px",
                fontSize: "15px",
                fontWeight: 600,
                backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                color: isDark ? "#fff" : "#333",
              }}
            >
              Hayır, İptal Et
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default Login;
